import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { t } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Crown, Sparkles, Coins, CreditCard, CheckCircle2, Loader2, Infinity as InfinityIcon, Gift, Copy, Check } from "lucide-react";

const PLAN_META = {
  basic:    { color: "bg-secondary text-primary" },
  advanced: { color: "bg-accent/20 text-accent" },
  ultimate: { color: "bg-primary text-primary-foreground" },
};

const PLAN_NAMES = {
  free:     {es:"Gratis",    en:"Free",      it:"Gratuito",  pt:"Grátis",     de:"Kostenlos",  zh:"免费",   ru:"Бесплатно",  ar:"مجاني",    hi:"मुफ़्त"},
  basic:    {es:"Básico",    en:"Basic",     it:"Base",      pt:"Básico",     de:"Basis",      zh:"基础",   ru:"Базовый",    ar:"أساسي",    hi:"बेसिक"},
  advanced: {es:"Avanzado",  en:"Advanced",  it:"Avanzato",  pt:"Avançado",   de:"Fortgeschritten", zh:"进阶", ru:"Продвинутый", ar:"متقدم", hi:"एडवांस्ड"},
  ultimate: {es:"Ilimitado", en:"Unlimited", it:"Illimitato",pt:"Ilimitado",  de:"Unbegrenzt", zh:"无限",   ru:"Безлимит",   ar:"غير محدود",hi:"असीमित"},
};

export default function PlanPage() {
  const { refreshUser } = useAuth();
  const { lang } = useLang();
  const [status, setStatus] = useState(null);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loadingTier, setLoadingTier] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([
      api.get("/plan/status"),
      api.get("/plan/referrals").catch(() => ({ data: null })),
    ]);
    setStatus(s.data);
    setReferral(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts >= 8) return;
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(t(lang, "payment_success"));
          setParams({});
          await load();
          await refreshUser?.();
          return;
        }
        if (data.status === "expired") {
          toast.error(t(lang, "payment_failed"));
          setParams({});
          return;
        }
        toast.info(t(lang, "payment_pending"), { id: "pay-poll" });
        setTimeout(poll, 2000);
      } catch (e) {
        console.warn("payment status poll failed", e?.message || e);
        toast.error(t(lang, "error"));
        setParams({});
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params, lang, load, refreshUser, setParams]);

  const redeem = async () => {
    setRedeeming(true);
    try {
      await api.post("/plan/redeem-points");
      toast.success(t(lang, "payment_success"));
      await load();
      await refreshUser?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t(lang, "error"));
    } finally {
      setRedeeming(false);
    }
  };

  const checkout = async (plan) => {
    setLoadingTier(plan);
    try {
      // When the app is embedded in a parent iframe (e.g. app.emergent.sh preview),
      // the origin returned by window.location.origin is the iframe's origin — which is
      // what the backend needs to build the success/cancel URLs. This is correct.
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan });

      // Stripe Checkout refuses to render inside iframes (X-Frame-Options: DENY).
      // If we're inside a cross-origin iframe, opening in a new tab is the only reliable path.
      const inIframe = window.self !== window.top;
      if (inIframe) {
        // Open in a new tab; user completes payment and returns to the app tab.
        const win = window.open(data.url, "_blank", "noopener,noreferrer");
        if (!win) {
          // Pop-up was blocked — instruct the user
          toast.error(t(lang, "popup_blocked") || "Enable pop-ups to open Stripe Checkout.");
          setLoadingTier(null);
          return;
        }
      } else {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("checkout failed", err);
      toast.error(err?.response?.data?.detail || t(lang, "error"));
      setLoadingTier(null);
    }
  };

  const shareLink = referral?.code
    ? `${window.location.origin}/register?ref=${referral.code}`
    : "";

  const copyReferral = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success(t(lang, "copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.warn("clipboard write failed", e?.message || e);
      toast.error(t(lang, "error"));
    }
  };

  if (!status) return <div className="text-muted-foreground">Loading…</div>;

  const currentPlan = status.plan;
  const isPaid = currentPlan !== "free";
  const pct = Math.min(100, Math.round((status.points / status.points_needed) * 100));
  const paidTiers = status.tiers.filter((tt) => tt.id !== "free");
  const capLabel = (cap) => cap < 0 ? t(lang, "unlimited") : `${cap} · ${t(lang, "day")}`;

  return (
    <div className="space-y-10">
      <div>
        <div className="text-xs uppercase tracking-widest text-accent font-medium">{t(lang, "plan")}</div>
        <h1 className="font-heading text-4xl font-light mt-2">{t(lang, "current_plan")}</h1>
      </div>

      {/* Current plan card */}
      <div className={`p-8 rounded-2xl border ${isPaid ? "border-accent bg-accent/10" : "border-border bg-card"}`} data-testid="plan-current">
        <div className="flex items-center gap-4 flex-wrap">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPaid ? "bg-accent text-accent-foreground" : "bg-secondary text-primary"}`}>
            <Crown className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-heading text-2xl font-medium flex items-center gap-2 flex-wrap" data-testid="current-plan-name">
              {PLAN_NAMES[currentPlan]?.[lang] || currentPlan}
              {status.admin_unlimited && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent text-accent-foreground uppercase tracking-widest font-sans" data-testid="creator-badge">
                  Creator · ∞
                </span>
              )}
            </div>
            {status.admin_unlimited ? (
              <div className="text-sm text-muted-foreground">
                Unlimited AI answers · lifetime access
              </div>
            ) : isPaid ? (
              <div className="text-sm text-muted-foreground">
                {status.days_left} {t(lang, "days_left")}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                5 {t(lang, "answers")} / {t(lang, "day")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Points card */}
      {!isPaid && (
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="plan-points-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary text-primary flex items-center justify-center">
              <Coins className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="font-heading text-xl font-medium">{t(lang, "points_to_basic")}</div>
              <div className="text-sm text-muted-foreground">{status.points} / {status.points_needed}</div>
            </div>
            <Button
              onClick={redeem}
              disabled={!status.can_redeem || redeeming}
              className="rounded-full gap-2"
              data-testid="redeem-points-btn"
            >
              {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
              {t(lang, "redeem_points")}
            </Button>
          </div>
          <Progress value={pct} />
        </div>
      )}

      {/* 3 paid tiers */}
      <div>
        <h2 className="font-heading text-2xl font-medium mb-4">{t(lang, "choose_plan")}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {paidTiers.map((tt) => {
            const meta = PLAN_META[tt.id];
            const highlighted = tt.id === "advanced";
            const isCurrent = tt.id === currentPlan;
            return (
              <div
                key={tt.id}
                data-testid={`tier-${tt.id}`}
                className={`p-6 rounded-2xl border-2 space-y-4 relative ${
                  highlighted ? "border-accent bg-accent/5" : "border-border bg-card"
                } ${isCurrent ? "ring-2 ring-primary" : ""}`}
              >
                {highlighted && (
                  <div className="absolute -top-3 left-6 px-2.5 py-0.5 rounded-full bg-accent text-accent-foreground text-[10px] uppercase tracking-widest">
                    {t(lang, "most_popular")}
                  </div>
                )}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${meta.color}`}>
                  {tt.daily_cap < 0 ? <InfinityIcon className="w-5 h-5" /> : <Sparkles className="w-5 h-5" strokeWidth={1.5} />}
                </div>
                <div>
                  <div className="font-heading text-2xl font-medium">
                    {PLAN_NAMES[tt.id]?.[lang] || tt.id}
                  </div>
                  <div className="text-3xl font-heading font-light mt-2">
                    ${tt.price_usd}<span className="text-sm text-muted-foreground font-normal">/{t(lang, "month")}</span>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground border-t border-border pt-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    <span data-testid={`tier-${tt.id}-cap`}>
                      {capLabel(tt.daily_cap)} {t(lang, "answers")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    <span>{t(lang, "premium_feat_1")}</span>
                  </div>
                </div>
                <Button
                  onClick={() => checkout(tt.id)}
                  disabled={loadingTier === tt.id || isCurrent || status.admin_unlimited}
                  className="w-full rounded-full gap-2"
                  variant={highlighted ? "default" : "outline"}
                  data-testid={`checkout-${tt.id}-btn`}
                >
                  {loadingTier === tt.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  {status.admin_unlimited ? "Creator access" : isCurrent ? t(lang, "current_plan") : `${t(lang, "upgrade")} · $${tt.price_usd}`}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Refer a friend */}
      {referral?.code && (
        <div className="p-6 rounded-2xl border-2 border-dashed border-accent/50 bg-accent/5 space-y-4" data-testid="referral-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0">
              <Gift className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-xl font-medium">{t(lang, "refer_friend")}</div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {t(lang, "refer_desc").replace("{days}", String(referral.reward_days))}
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-stretch">
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-background border border-border font-mono text-sm truncate" data-testid="referral-link">
              {shareLink}
            </div>
            <Button
              onClick={copyReferral}
              className="rounded-lg gap-2 whitespace-nowrap"
              variant={copied ? "outline" : "default"}
              data-testid="referral-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? t(lang, "copied") : t(lang, "copy_link")}
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">{t(lang, "your_referral_code")}:</span>
            <span className="font-mono font-medium tracking-wider text-accent" data-testid="referral-code">
              {referral.code}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{t(lang, "friends_referred")}</div>
              <div className="font-heading text-2xl font-medium mt-1" data-testid="referral-count">{referral.count}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{t(lang, "days_earned_free")}</div>
              <div className="font-heading text-2xl font-medium mt-1 text-accent" data-testid="referral-days-earned">
                {referral.days_earned}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Premium benefits */}
      <div>
        <h2 className="font-heading text-2xl font-medium mb-4">{t(lang, "premium_features")}</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {["premium_feat_1", "premium_feat_2", "premium_feat_3", "premium_feat_4"].map((k) => (
            <div key={k} className="flex items-start gap-2 p-4 rounded-xl border border-border bg-card">
              <Sparkles className="w-4 h-4 text-accent mt-1 shrink-0" strokeWidth={1.5} />
              <span className="text-sm">{t(lang, k)}</span>
            </div>
          ))}
        </div>
      </div>

      {isPaid && (
        <div className="p-6 rounded-xl border border-border bg-secondary/50 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-primary" />
          <span className="text-sm">{t(lang, "premium_active")}</span>
        </div>
      )}
    </div>
  );
}
